<template lang="pug">
//- The landing page. The copy on this page is also written out as static HTML
//- for crawlers and link previews (homeBodyHtml in build/seo-plugins.ts). When
//- the copy here changes, change it there too.
//-
//- The pictures carry what the words do not: the animated chat demo with the
//- bunny hopping along its top edge, one drawing per setup step, the bunny at
//- the centre of what it guards and reads, and a background of small glyphs
//- (a file, a chat bubble, a magnifier, a spark). All of it is drawn from the
//- theme tokens, so it follows light and dark like the rest of the site.
//-
//- The demo reply bubble, rendered once per scenario in the sizer (which fixes
//- the chat window's height) and live once the animation reaches the reply
//- step. `expr` is the template expression holding the scenario object ('cur'
//- for the live copy, 's' inside the sizer's v-for), spliced in at compile
//- time. The reply's intro is before + strong + mid + folder + ":", so each
//- scenario controls its own phrasing while the markup stays one shape.
//-
//- `lines` is the expression for how many of the reply's lines are out yet:
//- the text is line 1, the table's header 2, its rows 3 onward, then the
//- sources and the file. The live copy passes demo.lines, which runDemo()
//- counts up, so the bubble grows a line at a time; the sizer passes a number
//- past the last line.
//-
//- It is ONE bubble from the moment the bunny starts thinking: the bunny and
//- the waiting dots sit in its head row, and the reply is written into the same
//- bubble underneath. Swapping a "thinking" bubble for a "reply" bubble made
//- the bunny blink out and back in. The dots stay until line 1 is out.
mixin demoReply(expr, lines)
    .bq-demo-bubble&attributes(attributes)
        .bq-demo-bubble-head
            img.bq-demo-bunny(src="/bunnyquery-logo-cut-sm.png" alt="" aria-hidden="true")
            span.bq-demo-dots(v-if=`${lines} < 1` aria-hidden="true")
        p.bq-demo-reply-p(v-if=`${lines} >= 1`)
            | {{ #{expr}.reply.before }}
            strong {{ #{expr}.reply.strong }}
            | {{ #{expr}.reply.mid }}
            span(translate="no") {{ #{expr}.folder }}
            | :
        table.bq-demo-table(translate="no" v-if=`${lines} >= 2`)
            thead
                tr
                    th(v-for=`h in ${expr}.table.head` :key="h") {{ h }}
            tbody
                template(v-for=`(r, ri) in ${expr}.table.rows` :key="ri")
                    tr(v-if=`${lines} >= 3 + ri`)
                        td(v-for="(c, ci) in r" :key="ci") {{ c }}
        p.bq-demo-sources(v-if=`${lines} >= 3 + ${expr}.table.rows.length`)
            | Sources:&nbsp;
            span(translate="no") {{ #{expr}.sources }}
        span.bq-demo-file(translate="no" v-if=`${lines} >= 4 + ${expr}.table.rows.length`) {{ #{expr}.file }} &darr;
.bq-landing
    main
        //- ─── hero: the pitch on the left, the product at work on the right ───
        section#what.bq-hero
            .bq-wrap.bq-hero-grid
                .bq-hero-copy
                    //- The tagline is part of the h1: search engines read the h1
                    //- as the page's topic, and "BunnyQuery" alone does not say
                    //- what it is.
                    h1 BunnyQuery #[span.bq-hero-tagline AI Indexed Data Storage]
                    p.bq-hero-body #[span.wordset Upload your documents,] spreadsheets, images, notes, and business records. #[span.wordset BunnyQuery turns] them into usable database, so your AI can answer questions, #[span.wordset generate reports,] and summarize information.#[br]#[br]Talk to your data instead of digging through dashboards, forms, #[span.wordset folders, and files.]
                    p.bq-hero-line Turn your data into smarter decisions with BunnyQuery.
                    .bq-hero-tags
                        span.bq-hero-tag 50+ file formats
                        span.bq-hero-tag OCR for images &amp; PDFs
                        span.bq-hero-tag CSV report export
                        span.bq-hero-tag MCP compatible
                    .bq-hero-actions
                        router-link.bq-btn(
                            data-track="landing_hero_try",
                            :to="user?.user_id ? '/my-projects' : '/signup'"
                        ) Try BunnyQuery &rarr;
                .bq-hero-stage
                    //- The bunny hops back and forth along the top edge of the
                    //- chat window.
                    .bq-hero-landing-bunny
                        BunnyLoader(:overlay="false")
                    //- A screen showing the product's chat, not a chat client:
                    //- there is no input row, no Send and nothing to press, so
                    //- nobody tries to type into it. Everything happens in the
                    //- conversation itself, a line at a time: the folder's files
                    //- upload and come out indexed, the question is typed and
                    //- submitted, the answer arrives with its table, sources and
                    //- exportable CSV. The frame only carries the dashed plate
                    //- behind the window.
                    .bq-demo-frame
                        .bq-demo
                            .bq-demo-bar
                                span.bq-demo-bar-title(translate="no") BunnyQuery &middot; {{ cur.project }}
                            //- The conversation assembles itself: runDemo() (script)
                            //- walks demo.step through index -> type -> send -> reply
                            //- and loops; prefers-reduced-motion gets the finished
                            //- state. The stage holds TWO copies of the chat: the
                            //- finished one, invisible, fixes the window's height for
                            //- the whole loop; the live gated one sits absolutely
                            //- over it.
                            .bq-demo-stage
                                //- One finished copy of EVERY scenario, stacked in a
                                //- single grid cell: the tallest fixes the window's
                                //- height, so the window never resizes when the loop
                                //- rotates scenarios.
                                .bq-demo-chat--sizer(aria-hidden="true")
                                    .bq-demo-chat.bq-demo-sizer-copy(v-for="s in DEMO_SCENARIOS" :key="s.project")
                                        .bq-demo-note
                                            .bq-demo-note-head
                                                span.material-symbols-outlined.bq-demo-folder-icon(translate="no" aria-hidden="true") folder
                                                span.bq-demo-note-file(translate="no") {{ s.folder }}
                                            .bq-demo-note-done {{ s.indexedLabel }} &#10003;
                                        .bq-demo-msg.bq-demo-msg--user
                                            .bq-demo-bubble {{ s.question }}
                                        .bq-demo-msg
                                            +demoReply('s', '99')
                                .bq-demo-chat.bq-demo-chat--live
                                    //- The folder's row, two lines in both of its states
                                    //- so it never changes height. While the folder
                                    //- uploads it is the working (yellow) row: under the
                                    //- folder's name it shows each file as it goes up and
                                    //- counts them, filling from the left. Then it turns
                                    //- into the green row, the count of indexed files
                                    //- under the name.
                                    .bq-demo-note(
                                        v-if="demo.step >= DS.uploading"
                                        :class="{ 'is-working': demo.step < DS.indexed }"
                                        :style="{ '--att-progress': uploadPct + '%' }"
                                    )
                                        .bq-demo-note-head
                                            span.material-symbols-outlined.bq-demo-folder-icon(translate="no" aria-hidden="true") folder
                                            span.bq-demo-note-file(translate="no") {{ cur.folder }}
                                            template(v-if="demo.step < DS.indexed")
                                                span.bq-demo-note-state &middot; uploading
                                                span.bq-demo-dots(aria-hidden="true")
                                        //- the file going up right now, and how many are up
                                        .bq-demo-note-upload(v-if="demo.step < DS.indexed" translate="no")
                                            span.bq-demo-note-path {{ demo.file }}
                                            span.bq-demo-note-count ({{ fmtCount(demo.count) }}/{{ fmtCount(cur.total) }})
                                        .bq-demo-note-done(v-else) {{ cur.indexedLabel }} &#10003;
                                    //- The question is typed left to right in a draft
                                    //- bubble anchored on the left, so it grows the way
                                    //- writing does. (Typed in a right-aligned bubble it
                                    //- grew leftward and looked like it was sliding in.)
                                    .bq-demo-msg.bq-demo-msg--draft(v-if="demo.step === DS.typing")
                                        .bq-demo-bubble
                                            | {{ demo.typed }}
                                            span.bq-demo-caret(aria-hidden="true")
                                    //- Submitted: the draft is replaced by the finished
                                    //- message on the user's side.
                                    .bq-demo-msg.bq-demo-msg--user(v-if="demo.step >= DS.sent")
                                        .bq-demo-bubble {{ cur.question }}
                                    //- The bunny's bubble: thinking, then the reply
                                    //- written into that same bubble a line at a time.
                                    .bq-demo-msg(v-if="demo.step >= DS.thinking")
                                        +demoReply('cur', 'demo.lines')
                    p.bq-demo-caption Answers, reports, and exports come from #[span.wordset your own files,] not the open internet.

        //- ─── how: three steps, each with its drawing ───
        section#how.bq-section.bq-section--alt
            .bq-wrap
                .bq-section-header
                    .bq-section-heading
                        span.bq-pill HOW IT WORKS
                        h2 AI Assistant for #[span.wordset Your Business Data]
                    p.bq-section-sub Upload your files, then ask questions in natural language and get answers, reports, and insights back. #[span.wordset Setup takes three steps.]
                .bq-step-grid
                    .bq-step
                        //- A project card: its name, and the base prompt being
                        //- typed into it.
                        .bq-step-art(aria-hidden="true")
                            svg(viewBox="0 0 280 150" fill="none")
                                rect.ill-shadow(x="74" y="26" width="140" height="106")
                                rect.ill-surface(x="70" y="22" width="140" height="106")
                                rect.ill-accent(x="84" y="36" width="24" height="24")
                                path.ill-accent(d="M96 42v12M90 48h12")
                                line.ill-ink.ill-ink--thick(x1="118" y1="43" x2="182" y2="43")
                                line.ill-line(x1="118" y1="54" x2="162" y2="54")
                                rect.ill-field(x="84" y="72" width="112" height="42")
                                line.ill-line(x1="92" y1="83" x2="186" y2="83")
                                line.ill-line(x1="92" y1="93" x2="176" y2="93")
                                line.ill-line(x1="92" y1="103" x2="140" y2="103")
                                line.ill-accent.ill-caret(x1="146" y1="98" x2="146" y2="108")
                        .bq-step-body
                            h3 1. Create a project
                            p Name your project and add a base prompt: a short note on what your data is and how you want the agent to handle it.
                    .bq-step
                        //- A key on its way into the model.
                        .bq-step-art(aria-hidden="true")
                            svg(viewBox="0 0 280 150" fill="none")
                                circle.ill-ink.ill-ink--thick(cx="58" cy="75" r="15")
                                path.ill-ink.ill-ink--thick(d="M73 75h48M106 75v11M117 75v8")
                                path.ill-accent.ill-flow(d="M130 75H168")
                                rect.ill-shadow(x="176" y="47" width="64" height="64")
                                path.ill-ink(d="M186 43v-9M204 43v-9M222 43v-9M186 107v9M204 107v9M222 107v9M236 57h9M236 75h9M236 93h9")
                                rect.ill-surface(x="172" y="43" width="64" height="64")
                                path.ill-spark(transform="translate(204 75)" d="M0-15C1.6-5.4 5.4-1.6 15 0 5.4 1.6 1.6 5.4 0 15-1.6 5.4-5.4 1.6-15 0-5.4-1.6-1.6-5.4 0-15Z")
                        .bq-step-body
                            h3 2. Add your AI API key
                            p Choose ChatGPT or Claude, whichever model you prefer, and enter that platform's API key. Your own key powers your assistant.
                    .bq-step
                        //- Files going in, a conversation coming out.
                        .bq-step-art(aria-hidden="true")
                            svg(viewBox="0 0 280 150" fill="none")
                                path.ill-surface(d="M26 38h24l12 12v36H26zM50 38v12h12")
                                path.ill-surface(d="M40 50h24l12 12v36H40zM64 50v12h12")
                                path.ill-surface(d="M54 62h24l12 12v36H54zM78 62v12h12")
                                line.ill-line(x1="61" y1="84" x2="83" y2="84")
                                line.ill-line(x1="61" y1="92" x2="83" y2="92")
                                line.ill-line(x1="61" y1="100" x2="75" y2="100")
                                path.ill-accent.ill-flow(d="M100 84H138")
                                rect.ill-shadow(x="150" y="26" width="112" height="106")
                                rect.ill-surface(x="146" y="22" width="112" height="106")
                                rect.ill-bubble.ill-bubble--user(x="198" y="32" width="50" height="15")
                                rect.ill-bubble(x="156" y="55" width="86" height="46")
                                image(href="/bunnyquery-logo-cut-sm.png" x="160" y="59" width="15" height="14.5")
                                line.ill-line(x1="180" y1="67" x2="234" y2="67")
                                rect.ill-field(x="162" y="78" width="74" height="17")
                                path.ill-line(d="M162 86.5h74M186 78v17M211 78v17")
                                rect.ill-field(x="156" y="109" width="68" height="11")
                                rect.ill-send(x="229" y="109" width="19" height="11")
                        .bq-step-body
                            h3 3. Start #[span.wordset BunnyQuery-ing!]
                            p Upload files or whole folders. Even huge datasets are indexed for you. Then ask questions, request analysis, and export reports.

        //- ─── why: the bunny, and the four reasons ───
        section#why.bq-section
            .bq-wrap
                .bq-section-header
                    .bq-section-heading
                        span.bq-pill WHY BUNNYQUERY
                        h2 Your data. #[span.wordset Your AI.] #[span.wordset Your rules.]
                    p.bq-section-sub Use AI without giving up control of your data.
                .bq-why-grid
                    //- The bunny at the centre of a ring of tiles, one per reason
                    //- (and a chat bubble). One picture, named by the label.
                    .bq-why-scene(role="img" aria-label="BunnyQuery bunny inspecting data with a magnifying glass")
                        svg(viewBox="0 0 420 420" fill="none" aria-hidden="true")
                            circle.ill-orbit(cx="210" cy="210" r="160")
                            circle.ill-orbit.ill-orbit--inner(cx="210" cy="210" r="118")
                            path.ill-spark.ill-twinkle(transform="translate(118 112)" d="M0-11C1.2-4 4-1.2 11 0 4 1.2 1.2 4 0 11-1.2 4-4 1.2-11 0-4-1.2-1.2-4 0-11Z")
                            path.ill-spark.ill-twinkle.ill-twinkle--late(transform="translate(318 244)" d="M0-8C.9-2.9 2.9-.9 8 0 2.9.9.9 2.9 0 8-.9 2.9-2.9.9-8 0-2.9-.9-.9-2.9 0-8Z")
                            path.ill-spark.ill-twinkle.ill-twinkle--later(transform="translate(104 262)" d="M0-6C.7-2.2 2.2-.7 6 0 2.2.7.7 2.2 0 6-.7 2.2-2.2.7-6 0-2.2-.7-.7-2.2 0-6Z")
                            image.ill-bunny(href="/bunnyquery-logo-cut-low.png" x="112" y="118" width="196" height="189")
                            g(v-for="t in SCENE_TILES" :key="t.name")
                                rect.ill-shadow(:x="t.x - 23" :y="t.y - 23" width="52" height="52")
                                rect.ill-tile(:x="t.x - 26" :y="t.y - 26" width="52" height="52")
                                Glyph.ill-glyph(as="g" :shapes="t.icon" :transform="`translate(${t.x - 12} ${t.y - 12})`")
                    .bq-reasons
                        .bq-reason
                            Glyph.bq-icon(:shapes="ICON.shield")
                            .bq-reason-body
                                span.bq-reason-title Safe from AI platforms
                                p BunnyQuery prevents AI platforms from using your data to train their AI models or exposing your data.
                        .bq-reason
                            //- Database cylinder with a check at the corner: answers
                            //- come from a lookup you can verify, not from a model's memory.
                            Glyph.bq-icon(:shapes="ICON.source")
                            .bq-reason-body
                                span.bq-reason-title Your source of truth
                                p Your AI never answers from memory. Nothing is trained on your data: every answer comes from a lookup in your own database, and every record traces back to the file, sheet, and row it came from. Wrong answers are traceable and corrections are a single edit, not a retrain.
                        .bq-reason
                            Glyph.bq-icon(:shapes="ICON.formats")
                            .bq-reason-body
                                span.bq-reason-title Reads basically everything, Supported formats include:
                                p #[span(translate="no") .doc, .docx, .docm, .xls, .xlsx, .xlsm, .ppt, .pptx, .pptm, .hwp, .hwpx, .ods, .odt, .odp, .epub, .eml, .pdf, .jpg, .jpeg, .png, .gif, .webp, .html, .htm, .xml, .svg, .csv, .tsv, .tab, .txt, .text, .log, .md, .markdown, .rst] ...and a lot more.
                        .bq-reason
                            Glyph.bq-icon(:shapes="ICON.team")
                            .bq-reason-body
                                span.bq-reason-title Built for collaboration
                                p Invite teammates, clients, or customers to ask questions directly. They get answers, reports, and the information they need without waiting on you.

        //- ─── for developers ───
        section#dev.bq-section.bq-section--alt
            .bq-wrap
                .bq-section-header
                    .bq-section-heading
                        span.bq-pill FOR DEVELOPERS
                        SkapiBunnyLockup.bq-dev-lockup
                        h2 Expandable with Developer Options
                    p.bq-section-sub BunnyQuery is built on Skapi, a serverless platform that scales with zero downtime. #[span.wordset Your project] is compatible with Skapi backend: connect it over MCP, embed the chat widget, or build your own web service on top.
                .bq-card-grid
                    //- Whole card is the anchor (like the news cards); the url line
                    //- is a span since anchors can't nest.
                    a.bq-card(href="https://mcp.broadwayinc.computer" target="_blank" rel="noopener" data-track="landing_card_mcp")
                        Glyph.bq-icon(:shapes="ICON.mcp")
                        h3 MCP Connectivity
                        p Skapi MCP connects your project's context database to any AI platform that supports MCP.
                        span.bq-card-more(translate="no") mcp.broadwayinc.computer &rarr;
                    a.bq-card(href="https://www.skapi.com" target="_blank" rel="noopener" data-track="landing_card_skapi")
                        Glyph.bq-icon(:shapes="ICON.code")
                        h3 Develop with Skapi
                        p Connect your context database with Skapi and build your own web services.
                        span.bq-card-more(translate="no") www.skapi.com &rarr;
                    a.bq-card(href="https://github.com/broadwayinc/bunnyquery" target="_blank" rel="noopener" data-track="landing_card_github")
                        Glyph.bq-icon(:shapes="ICON.widget")
                        h3 HTML Embeddable Chat Widget
                        p Embed a full chat UI with one script tag, or import the engine to build your own.
                        span.bq-card-more BunnyQuery Widget &rarr;

        //- ─── latest articles; hidden until the list loads, for good if it
        //- fails (a marketing page shows no error states) ───
        section#news.bq-section(v-if="newsArticles.length")
            .bq-wrap
                .bq-section-header
                    .bq-section-heading
                        span.bq-pill NEWS &amp; UPDATES
                        h2 Latest from BunnyQuery
                    p.bq-section-sub Release notes, announcements, and stories from the team.
                .bq-card-grid
                    router-link.bq-card(
                        v-for="a in newsArticles"
                        :key="a.slug"
                        :to="`/articles/${a.slug}`"
                        data-track="landing_article"
                    )
                        time.bq-news-date(translate="no") {{ a.date }}
                        h3 {{ a.title }}
                        p.bq-news-excerpt(v-if="a.excerpt") {{ a.excerpt }}
                        span.bq-card-more(aria-hidden="true") Read &rarr;
                p.bq-section-link
                    router-link.bq-cta-link(to="/articles" data-track="landing_articles_all")
                        | All articles
                        span.bq-arrow.bq-arrow--trail(translate="no") &rarr;

        //- ─── pricing ───
        //- The cards are the subscription page's own PlanCards, so a price,
        //- description or limit can never differ between the two. Each card is
        //- one link stretched over the whole card: a signed-in visitor gets the
        //- New Project dialog right here with that plan chosen (Free creates and
        //- opens the project, a paid plan goes to checkout), anyone else goes to
        //- the signup page.
        section#pricing.bq-section.bq-section--alt
            .bq-wrap
                .bq-section-header
                    .bq-section-heading
                        span.bq-pill PRICING
                        h2 Start Free, #[span.wordset Scale as You Grow]
                    p.bq-section-sub Every project starts on Free. Upgrade when it needs more.
                PlanCards.bq-pricing-cards(:showFreePlan="true")
                    template(#freeAction)
                        a.bq-plan-link(v-if="user?.user_id" href="/my-projects" @click.prevent="openCreate('free')" data-track="landing_pricing_free")
                            span.bq-btn.bq-plan-cta.bq-plan-cta--ghost Start for Free
                        router-link.bq-plan-link(v-else to="/signup" data-track="landing_pricing_free")
                            span.bq-btn.bq-plan-cta.bq-plan-cta--ghost Start for Free
                    template(#standardAction)
                        a.bq-plan-link(v-if="user?.user_id" href="/my-projects" @click.prevent="openCreate('standard')" data-track="landing_pricing_standard")
                            span.bq-btn.bq-plan-cta.bq-plan-cta--ghost Choose Standard
                        router-link.bq-plan-link(v-else to="/signup" data-track="landing_pricing_standard")
                            span.bq-btn.bq-plan-cta.bq-plan-cta--ghost Choose Standard
                    template(#premiumAction)
                        a.bq-plan-link(v-if="user?.user_id" href="/my-projects" @click.prevent="openCreate('premium')" data-track="landing_pricing_premium")
                            span.bq-btn.bq-plan-cta Choose Premium
                        router-link.bq-plan-link(v-else to="/signup" data-track="landing_pricing_premium")
                            span.bq-btn.bq-plan-cta Choose Premium
                //- The cards list headline figures only; how sends are counted,
                //- what the hard stop means and the rest are in the docs.
                p.bq-section-link
                    a.bq-cta-link(:href="PLAN_DOCS.plans" target="_blank" rel="noopener" data-track="landing_pricing_limits")
                        | How plan limits work
                        span.bq-arrow.bq-arrow--trail(translate="no") &rarr;

        //- ─── closing panel, on the hero's glyph pattern ───
        section.bq-section.bq-close
            .bq-wrap
                .bq-close-panel
                    .bq-landing-bunny
                        BunnyLoader(:overlay="false")
                    h2 Try BunnyQuery #[span.wordset for Free]
                    p Free to start. #[span.wordset No credit card required.]
                    router-link.bq-btn(
                        data-track="landing_bottom_try",
                        :to="user?.user_id ? '/my-projects' : '/signup'"
                    ) Try BunnyQuery &rarr;

    //- From a plan card, signed in: name the project here, then Free creates and
    //- opens it and a paid plan goes to checkout (views/new-project.ts).
    CreateProject(
        :showCreateModal="showCreate"
        :initialPlan="createPlan"
        @close="showCreate = false"
    )
</template>

<script setup>
import { user } from "@/code/user";
import BunnyLoader from "@/components/bunnyLoader.vue";
import SkapiBunnyLockup from "@/components/skapiBunnyLockup.vue";
import PlanCards from "@/components/planCards.vue";
import CreateProject from "@/views/project-create.vue";
import { PLAN_DOCS } from "@/code/plan-copy";
import { removeFullScreenLoader } from "@/components/jsComponents";
import { getArticles } from "@/code/articles";
import { computed, h, onMounted, onUnmounted, reactive, ref } from "vue";

// Latest articles for the news section (newest first; section hides when
// empty or when the article list fails to load).
const newsArticles = ref([]);

// A plan card opens the New Project dialog with that plan chosen.
const createPlan = ref("");
const showCreate = ref(false);
const openCreate = (plan) => {
    createPlan.value = plan;
    showCreate.value = true;
};

// ─── glyphs ───
// Feather-style 24x24 glyphs, as data, because a glyph can be drawn twice: in
// its section and on a tile of the bunny's ring. A shape is [tag, attributes,
// accent]; an accent shape is stroked in the brand pink.
const ICON = {
    shield: [
        ["path", { d: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" }],
        ["circle", { cx: 12, cy: 10, r: 1.8 }, true],
        ["path", { d: "M12 11.8v3.2" }, true],
    ],
    // Database cylinder with a check at the corner: answers come from a
    // lookup you can verify, not from a model's memory.
    source: [
        ["ellipse", { cx: 11, cy: 5, rx: 8, ry: 3 }],
        ["path", { d: "M3 5v14c0 1.66 3.58 3 8 3 .7 0 1.37-.03 2-.1" }],
        ["path", { d: "M3 12c0 1.66 3.58 3 8 3 1.4 0 2.7-.14 3.8-.4" }],
        ["path", { d: "M19 5v6" }],
        ["path", { d: "M14.5 18.5l2.5 2.5 5-5" }, true],
    ],
    // a file under a magnifier
    formats: [
        ["path", { d: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" }],
        ["path", { d: "M14 2v6h6" }],
        ["circle", { cx: 10.5, cy: 13.5, r: 2.8 }, true],
        ["path", { d: "M12.7 15.7L15 18" }, true],
    ],
    team: [
        ["path", { d: "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" }],
        ["circle", { cx: 9, cy: 7, r: 4 }],
        ["path", { d: "M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" }, true],
    ],
    // a chat bubble with a question going up in it
    chat: [
        ["path", { d: "M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" }],
        ["path", { d: "M12 15v-5M9.5 12L12 9.5 14.5 12" }, true],
    ],
    mcp: [
        ["circle", { cx: 18, cy: 5, r: 3 }],
        ["circle", { cx: 6, cy: 12, r: 3 }, true],
        ["circle", { cx: 18, cy: 19, r: 3 }],
        ["path", { d: "M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98" }],
    ],
    code: [
        ["path", { d: "M16 18l6-6-6-6" }],
        ["path", { d: "M8 6l-6 6 6 6" }, true],
    ],
    // a page with a chat bubble in it
    widget: [
        ["rect", { x: 2, y: 3, width: 20, height: 17 }],
        ["path", { d: "M2 8h20" }],
        ["path", { d: "M8 12h8v4h-5l-2 2v-2H8z" }, true],
    ],
};

// One glyph. On its own it is an svg element in a span; inside a drawing it is
// a g (as="g") and inherits the drawing's stroke. The accent is set inline:
// the shapes are made here, outside the template, so they carry no scope
// attribute for a scoped rule to match.
const Glyph = (props, { attrs }) => {
    const shapes = props.shapes.map(([tag, a, accent]) =>
        h(tag, accent ? { ...a, style: { stroke: "var(--bq-pink)" } } : a),
    );
    if (props.as === "g") return h("g", attrs, shapes);
    return h("span", { ...attrs, "aria-hidden": "true" }, [
        h("svg", {
            width: 24,
            height: 24,
            viewBox: "0 0 24 24",
            fill: "none",
            stroke: "currentColor",
            "stroke-width": 2,
            "stroke-linecap": "round",
            "stroke-linejoin": "round",
        }, shapes),
    ]);
};
Glyph.props = ["shapes", "as"];

// The "Why" drawing: five tiles evenly spaced on a ring round the bunny, the
// four reasons' glyphs and a chat bubble, starting at twelve o'clock.
const SCENE_TILES = ["shield", "source", "team", "formats", "chat"].map((name, i) => {
    const a = ((-90 + i * 72) * Math.PI) / 180;
    return {
        name,
        icon: ICON[name],
        x: Math.round(210 + 160 * Math.cos(a)),
        y: Math.round(210 + 160 * Math.sin(a)),
    };
});

/* ---- hero chat-demo animation -------------------------------------------
 * A scripted loop through the product's happy path: upload -> indexed ->
 * type the question -> send -> thinking -> reply. Pure presentation state; the
 * template gates each piece on demo.step so the conversation assembles itself,
 * all of it inside the chat screen (the window has no input row to type in).
 * Each pass through the loop plays the next scenario, one per use-case card
 * (finance / business knowledge / personal health / research), so the one chat
 * window demos all four. A reply is before + strong + mid + folder + ":".
 * `total` is the file count in indexedLabel, which the upload counts up to;
 * `uploads` are the file names the upload row flashes through on the way.
 */
const DS = { idle: 0, uploading: 1, indexed: 2, typing: 3, sent: 4, thinking: 5, replied: 6 };
const DEMO_SCENARIOS = [
    {
        // 001 Finance Assistant
        project: "business-finance",
        folder: "receipts_invoice",
        indexedLabel: "3,812 files indexed",
        total: 3812,
        uploads: [
            "receipts/dec-13.pdf",
            "receipts/tomas_scan.jpg",
            "invoices/invoiceFinal.doc",
            "receipts/fuel_0402.png",
            "invoices/INV-2041.pdf",
            "bank-statements.pdf",
            "receipts/client_lunch.jpg",
            "invoices/wholesale_apr.xlsx",
            "receipts/parking-0412.pdf",
            "invoices/INV-2042.pdf",
            "receipts/courier.jpeg",
            "invoices/online-store_may.csv",
        ],
        question: "Break down my income and list my revenue sources.",
        reply: {
            before: "Income is up ",
            strong: "+11%",
            mid: " this quarter. Here is the monthly breakdown, compiled from the receipts and statements in ",
        },
        table: {
            head: ["Month", "Income", "Top source"],
            rows: [
                ["Apr", "$86k", "Wholesale"],
                ["May", "$94k", "Online store"],
                ["Jun", "$103k", "Online store"],
            ],
        },
        sources: "receipts/ (1,204 files) · bank-statements.pdf",
        file: "income-by-month.csv",
    },
    {
        // 002 Business Knowledge AI
        project: "team-ops",
        folder: "vendor_docs",
        indexedLabel: "5,309 files indexed",
        total: 5309,
        uploads: [
            "purchase-orders/PO-0087.pdf",
            "vendor-list.xlsx",
            "contracts/hanil-metals.docx",
            "purchase-orders/PO-0088.pdf",
            "quotes/steel_q3.pdf",
            "emails/park_delivery.eml",
            "purchase-orders/PO-0089.pdf",
            "contracts/nda_signed.pdf",
            "quotes/freight-jul.xlsx",
            "invoices/hanil_0728.pdf",
            "notes/supplier-call.txt",
            "purchase-orders/PO-0090.pdf",
        ],
        question: "Who is our biggest supplier and their contacts?",
        reply: {
            before: "Your biggest supplier is ",
            strong: "Hanil Metals",
            mid: ": $1.2M across 87 purchase orders this year. Their contacts, pulled from the orders and vendor list in ",
        },
        table: {
            head: ["Name", "Role", "Last contact"],
            rows: [
                ["J. Park", "Account manager", "Jul 28"],
                ["S. Lee", "Billing", "Jul 12"],
                ["M. Choi", "Logistics", "Jun 30"],
            ],
        },
        sources: "purchase-orders/ (87 files) · vendor-list.xlsx",
        file: "supplier-contacts.csv",
    },
    {
        // 003 Personal Health Assistant
        project: "my-health",
        folder: "medical_results",
        indexedLabel: "214 files indexed",
        total: 214,
        uploads: [
            "lab-reports/2026-03-04.pdf",
            "prescriptions/rx_0312.jpg",
            "lab-reports/2026-05-12.pdf",
            "scans/chest-xray.png",
            "prescriptions/rx_0518.jpg",
            "lab-reports/2026-07-21.pdf",
            "checkup/summary.docx",
            "prescriptions/rx_0722.jpg",
            "insurance/claim-form.pdf",
            "notes/diet-log.txt",
        ],
        question: "Show how my blood sugar has changed over time.",
        reply: {
            before: "Your fasting glucose is trending down, ",
            strong: "-14 mg/dL",
            mid: " since March. Compiled from the lab reports in ",
        },
        table: {
            head: ["Date", "Fasting", "HbA1c"],
            rows: [
                ["Mar 04", "118 mg/dL", "6.1%"],
                ["May 12", "109 mg/dL", "5.8%"],
                ["Jul 21", "104 mg/dL", "5.6%"],
            ],
        },
        sources: "lab-reports/ (9 files) · prescriptions/ (32 files)",
        file: "glucose-trend.csv",
    },
    {
        // 004 Research and Document Analysis
        project: "research-lab",
        folder: "research_papers",
        indexedLabel: "12 files indexed",
        total: 12,
        uploads: [
            "solid-electrolytes_2019.pdf",
            "cycle-life-study.pdf",
            "cathode-degradation.pdf",
            "fast-charging_2021.pdf",
            "li-metal-anodes.pdf",
            "electrolyte-review.pdf",
            "capacity-fade_2022.pdf",
            "interface-stability.pdf",
            "dendrite-growth.pdf",
            "thermal-runaway.pdf",
            "sulfide-conductors_2024.pdf",
            "pack-aging_2025.pdf",
        ],
        question: "Summarize the key findings from these research papers.",
        reply: {
            before: "The 12 papers converge on ",
            strong: "3 key findings",
            mid: ", each cited back to its source in ",
        },
        table: {
            head: ["#", "Finding", "Papers"],
            rows: [
                ["1", "Solid electrolytes double cycle life", "7 of 12"],
                ["2", "Degradation starts at the cathode", "5 of 12"],
                ["3", "Fast charging trades 9% capacity", "4 of 12"],
            ],
        },
        sources: "research_papers/ (12 files, 2019-2025)",
        file: "key-findings.csv",
    },
];
// `lines` is how many lines of the reply are out so far (see the demoReply
// mixin for what each one is).
// `count` and `file` are the upload: how many files are up and the one going
// up right now.
const demo = reactive({ step: DS.idle, count: 0, file: "", typed: "", lines: 0, scenario: 0 });
// How long the reply waits between one line and the next.
const REPLY_LINE_MS = 260;
const cur = computed(() => DEMO_SCENARIOS[demo.scenario]);
// The upload row's fill, and its counts with the same thousands separator
// the "indexed" label uses.
const uploadPct = computed(() => Math.round((100 * demo.count) / cur.value.total));
const fmtCount = (n) => n.toLocaleString("en-US");
// The upload: how many times the row changes, and how often.
const UPLOAD_TICKS = 28;
const UPLOAD_TICK_MS = 70;

let demoTimers = [];
// One ticker handle for the upload, then the typewriter, then the reply's
// lines. In a
// foreground tab the phases never overlap, but a throttled background tab can
// stretch one into the next, so every taker clears the handle before
// reassigning it (an overwritten interval would leak forever).
let demoTicker = null;
const clearDemoTicker = () => {
    if (demoTicker) { clearInterval(demoTicker); demoTicker = null; }
};
const demoLater = (fn, ms) => demoTimers.push(setTimeout(fn, ms));

const runDemo = () => {
    // Everything scheduled by the previous cycle has fired by the time the
    // loop re-enters (runDemo IS the last timer), so drop the spent handles.
    demoTimers = [];
    // A throttled background tab can leave the previous phase's interval
    // running into this one; clear it before anything reassigns the handle.
    clearDemoTicker();
    demo.step = DS.idle;
    demo.count = 0;
    demo.file = "";
    demo.typed = "";
    demo.lines = 0;
    const scenario = DEMO_SCENARIOS[demo.scenario];
    // The folder uploads: every tick a different file name flashes by and the
    // count climbs toward the folder's total. A folder with no more files than
    // names shows each file once, in step with its count; a bigger one cycles
    // the names while the count jumps.
    demoLater(() => {
        demo.step = DS.uploading;
        demo.file = scenario.uploads[0];
        let tick = 0;
        demoTicker = setInterval(() => {
            tick += 1;
            const { total, uploads } = scenario;
            demo.count = Math.min(total, Math.round((total * tick) / UPLOAD_TICKS));
            demo.file = total <= uploads.length
                ? uploads[Math.min(demo.count, uploads.length - 1)]
                : uploads[tick % uploads.length];
            if (tick >= UPLOAD_TICKS && demoTicker) { clearInterval(demoTicker); demoTicker = null; }
        }, UPLOAD_TICK_MS);
    }, 400);
    // Everything is up: the full count holds for a moment, then the row turns
    // into the green "indexed" row. Throttled, the upload ticker may not have
    // finished; finish it here.
    const uploadedAt = 400 + UPLOAD_TICKS * UPLOAD_TICK_MS + 350;
    demoLater(() => { clearDemoTicker(); demo.count = scenario.total; demo.step = DS.indexed; }, uploadedAt);
    const question = scenario.question;
    const typeAt = uploadedAt + 700;
    demoLater(() => {
        demo.step = DS.typing;
        // An earlier ticker may still be running in a throttled tab; take
        // the handle over cleanly instead of orphaning the old interval.
        clearDemoTicker();
        let i = 0;
        demoTicker = setInterval(() => {
            i += 1;
            demo.typed = question.slice(0, i);
            if (i >= question.length && demoTicker) { clearInterval(demoTicker); demoTicker = null; }
        }, 36);
    }, typeAt);
    // Questions differ in length per scenario, so everything after the
    // typewriter is scheduled off its real duration, not a fixed offset.
    const sendAt = typeAt + question.length * 36 + 500;
    // Sent: the draft gives way to the finished message on the user's side.
    // Clear the typewriter here too: throttled, it could outlive the send.
    demoLater(() => { clearDemoTicker(); demo.typed = ""; demo.step = DS.sent; }, sendAt);
    demoLater(() => { demo.step = DS.thinking; }, sendAt + 400);
    // The reply is written out a line at a time, into the bubble the bunny
    // has been thinking in: the text, the table's header, each of its rows,
    // the sources, the file.
    const replyLines = 4 + scenario.table.rows.length;
    demoLater(() => {
        demo.step = DS.replied;
        clearDemoTicker();
        demoTicker = setInterval(() => {
            demo.lines += 1;
            if (demo.lines >= replyLines && demoTicker) { clearInterval(demoTicker); demoTicker = null; }
        }, REPLY_LINE_MS);
    }, sendAt + 1900);
    // Hold the finished conversation for 3.5s, then play the next scenario.
    demoLater(() => {
        demo.scenario = (demo.scenario + 1) % DEMO_SCENARIOS.length;
        runDemo();
    }, sendAt + 1900 + replyLines * REPLY_LINE_MS + 3500);
};

onMounted(() => {
    removeFullScreenLoader();
    getArticles()
        .then(({ articles }) => { newsArticles.value = articles.slice(0, 3); })
        .catch(() => { /* section stays hidden */ });
    // Motion-sensitive visitors get the finished conversation, statically.
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        demo.lines = 99;
        demo.step = DS.replied;
        return;
    }
    runDemo();
});

onUnmounted(() => {
    demoTimers.forEach(clearTimeout);
    demoTimers = [];
    clearDemoTicker();
});
</script>

<style lang="less" scoped>
@import "@/assets/less/bq-tokens.less";

/* A field of small glyphs (a file, a chat bubble, a magnifier, a spark), used
   as a MASK over a flat colour so the pattern takes a theme token and follows
   light and dark. Each use pairs it with a fade, intersected with it. */
@glyphs: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180' viewBox='0 0 180 180' fill='none' stroke='%23000' stroke-width='1.3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M20 16h8l5 5v13H20zM28 16v5h5'/%3E%3Cpath d='M106 24h18v12h-10l-4 4v-4h-4z'/%3E%3Ccircle cx='52' cy='108' r='6'/%3E%3Cpath d='M56.5 112.5l5 5'/%3E%3Cpath d='M138 104c1 5 3 7 8 8-5 1-7 3-8 8-1-5-3-7-8-8 5-1 7-3 8-8z'/%3E%3C/svg%3E");
/* Dots for the drawings' own panels. */
.dot-grid(@size: 16px) {
    background-image: radial-gradient(@line 1px, transparent 1.5px);
    background-size: @size @size;
}

.bq-landing {
    min-height: 100vh;
    margin: 0;
    background: @paper;
    color: @ink;
    font-family: @mono;
    font-size: 16px;
    line-height: 1.7;
    overflow-x: hidden;

    button {
        all: unset;
        box-sizing: border-box;
        cursor: pointer;

        &::after {
            display: none;
        }
    }

    hr {
        background: @line;
    }
}

/* Sections are full-bleed so a band can carry its own surface; the content
   column, the navbar's width, lives in .bq-wrap. */
.bq-wrap {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
}

/* ─── button ─── */
.bq-btn {
    display: inline-block;
    padding: 0.9rem 1.75rem;
    border: 1px solid @ink;
    background: @ink;
    color: @paper;
    font: inherit;
    font-size: 0.92rem;
    text-decoration: none;
    cursor: pointer;
    /* hover extrude + press-down come from the global a.bq-btn rules in
       base.less, shared with every block button */
    transition: transform 0.12s ease, box-shadow 0.12s ease;
    box-shadow: 2px 2px var(--bq-shadow);
}

/* ─── hero ─── */
/* The glyph field sits behind the content, strongest in the top right corner
   behind the chat window and gone before it reaches the copy. */
.bq-hero {
    position: relative;
    isolation: isolate;
    padding: 4rem 0 4.5rem;
    border-bottom: 1px solid @line;

    &::before {
        content: "";
        position: absolute;
        inset: 0;
        z-index: -1;
        pointer-events: none;
        background-color: @line;
        -webkit-mask-image: radial-gradient(110% 120% at 100% 0%, #000 25%, transparent 72%), @glyphs;
        mask-image: radial-gradient(110% 120% at 100% 0%, #000 25%, transparent 72%), @glyphs;
        -webkit-mask-size: 100% 100%, 180px 180px;
        mask-size: 100% 100%, 180px 180px;
        -webkit-mask-repeat: no-repeat, repeat;
        mask-repeat: no-repeat, repeat;
        -webkit-mask-composite: source-in;
        mask-composite: intersect;
    }

    h1 {
        margin: 0;
        font-size: clamp(2.7rem, 5.4vw, 4.6rem);
        line-height: 1.05;
        font-weight: 700;
        letter-spacing: -0.02em;
        text-shadow: var(--bq-text-shadow);
    }
}

.bq-hero-grid {
    display: grid;
    /* minmax(0, ...) so neither column can be widened by its content */
    grid-template-columns: minmax(0, 0.88fr) minmax(0, 1.12fr);
    gap: 3.5rem;
    align-items: center;
}

/* The "AI Indexed Data Storage" line inside the hero h1: a line of its own
   under the name, in the brand pink, without the h1's shadow. */
.bq-hero-tagline {
    display: block;
    margin: 1.1rem 0 0;
    color: @pink;
    font-size: clamp(1.05rem, 1.7vw, 1.3rem);
    font-weight: 600;
    line-height: 1.5;
    letter-spacing: 0;
    text-shadow: none;
}

.bq-hero-body {
    margin: 1.75rem 0 0;
    color: @muted;
}

.bq-hero-line {
    margin: 1.5rem 0 0;
    font-weight: 600;
}

.bq-hero-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin: 1.25rem 0 0;
}

.bq-hero-tag {
    padding: 0.25rem 0.6rem;
    border: 1px solid @line;
    background: @paper;
    color: @muted;
    font-size: 0.78rem;
    white-space: nowrap;
}

.bq-hero-actions {
    margin-top: 2.25rem;
}

.bq-hero-stage {
    min-width: 0;
}

/* The bunny on the chat window's top edge. BunnyLoader's own rules (main.less)
   hop it 15ch from a left anchor; here it is re-anchored to the middle of the
   window (margin, not transform: the flip keyframes own `transform` on that
   element) and travels --hop either side.

   The hop is two frames stacked in one box: the standing frame at the bottom,
   whose feet land on the window's border, and the jumping frame a full
   bunny-height above it. The track has to be tall enough for BOTH (each is
   three lines at 1.2 line height, 3.6rem), or the jump is drawn outside it;
   and nothing here clips, so no frame can be cut off whatever it measures.
   An earlier version left the track at one frame's height inside a clipping
   stage, and the bunny vanished for the quarter of each second it was in the
   air.

   --hop is capped at half the track less the bunny's own half-width and a
   little air, so on a narrow phone it turns round before the window's edge
   (the percentage resolves against the track, which is as wide as the
   window). */
.bq-hero-landing-bunny {
    position: relative;
    width: 100%;
    --hop: min(21ch, calc(50% - 5ch));

    :deep(.bq-bunny-loader) {
        align-items: center;
        gap: 0;
    }

    :deep(.bq-bunny-stage) {
        width: 100%;
    }

    :deep(.bq-bunny-track) {
        height: 7.2rem;
        animation-name: bq-hero-bunny-move;
    }

    :deep(.bq-bunny-dir) {
        left: 50%;
        margin-left: -4ch;
    }
}

@keyframes bq-hero-bunny-move {
    0%     { transform: translateX(calc(var(--hop) * -1)); }
    25%    { transform: translateX(var(--hop)); }
    37.5%  { transform: translateX(var(--hop)); }
    50%    { transform: translateX(var(--hop)); }
    75%    { transform: translateX(calc(var(--hop) * -1)); }
    87.5%  { transform: translateX(calc(var(--hop) * -1)); }
    100%   { transform: translateX(calc(var(--hop) * -1)); }
}

/* A dashed pink plate behind the chat window, offset the way the site's hard
   shadows are. */
.bq-demo-frame {
    position: relative;
    isolation: isolate;

    &::before {
        content: "";
        position: absolute;
        inset: 14px -14px -14px 14px;
        z-index: -1;
        border: 1px dashed @pink;
        pointer-events: none;
    }
}

/* ─── hero chat demo ─── */
/* A screen, not a client: a title bar and the conversation, nothing else. */
.bq-demo {
    border: 1px solid @line;
    background: @paper;
    box-shadow: 4px 4px var(--bq-shadow);
    font-size: 0.88rem;
}

.bq-demo-bar {
    padding: 0.55rem 0.9rem;
    border-bottom: 1px solid @line;
    color: @muted;
    font-size: 0.8rem;
}

.bq-demo-chat {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
    padding: 1.1rem;
}

/* The invisible finished conversations reserve the height; the live copy
   plays on top of them, so the window never resizes mid-loop. */
.bq-demo-stage {
    position: relative;
    overflow: hidden;
}

/* Every scenario's finished conversation overlaps in ONE grid cell, so the
   tallest of them sets the window's height for the whole rotation. */
.bq-demo-chat--sizer {
    visibility: hidden;
    pointer-events: none;
    display: grid;

    * {
        animation: none !important;
    }
}

.bq-demo-sizer-copy {
    grid-area: 1 / 1;
    min-width: 0;
}

.bq-demo-chat--live {
    position: absolute;
    inset: 0;
}

/* The folder's row: green once indexed. Always two lines, the folder's name
   over what is happening to it, so the row is one height in both states. */
.bq-demo-note {
    position: relative;
    isolation: isolate;
    overflow: hidden;
    align-self: stretch;
    padding: 0.45rem 0.8rem;
    border: 1px solid @success-border;
    background: @success-bg;
    font-size: 0.78rem;
}

/* The row's first line: the folder, and "uploading" beside it while it goes
   up. One line always: on a narrow phone the folder's name is cut with an
   ellipsis before anything wraps. */
.bq-demo-note-head {
    display: flex;
    align-items: center;
    min-width: 0;
}

.bq-demo-note-file {
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.bq-demo-note-state {
    flex-shrink: 0;
    /* Pug emits no whitespace between sibling inline tags, so the gap after
       the filename has to be layout, not text. */
    margin-left: 0.3rem;
    color: @warning;
    white-space: nowrap;
}

.bq-demo-note .bq-demo-dots,
.bq-demo-folder-icon {
    flex-shrink: 0;
}

/* While the folder is still going up: the working (warning) tint, the same
   colour language as the real indexing row, with the real attachment chip's
   progress fill behind the text (a ::before whose width follows
   --att-progress). */
.bq-demo-note.is-working {
    border-color: @warning-border;
    background: @warning-bg;

    &::before {
        content: "";
        position: absolute;
        inset: 0 auto 0 0;
        z-index: -1;
        width: var(--att-progress, 0%);
        background: color-mix(in srgb, @warning-border 28%, transparent);
        transition: width 0.15s linear;
    }
}

/* The row's second line, the same size and spacing in both states. */
.bq-demo-note-upload,
.bq-demo-note-done {
    margin-top: 0.15rem;
    font-size: 0.72rem;
}

/* Indexed: how many files, and the check. One line, like the upload's. */
.bq-demo-note-done {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    color: @success;
}

/* Uploading: the file going up now, and the count at the row's right edge.
   The name is cut with an ellipsis, never wrapped, so the row keeps its
   height while names of every length flash through it. */
.bq-demo-note-upload {
    display: flex;
    align-items: baseline;
    gap: 0.75rem;
    color: @warning;
}

.bq-demo-note-path {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    opacity: 0.85;
}

/* figures of one width, so the count does not jitter as it climbs */
.bq-demo-note-count {
    flex-shrink: 0;
    font-variant-numeric: tabular-nums;
}

.bq-demo-msg {
    display: flex;
    align-items: flex-end;
    gap: 0.6rem;
    max-width: 92%;
}

.bq-demo-msg--user {
    align-self: flex-end;
    max-width: 85%;

    .bq-demo-bubble {
        background: @pink-bg;
        /* flat like the real user bubbles: the shadow stays on replies only */
        box-shadow: none;
    }
}

/* The question while it is being written: the user's colours, but anchored on
   the left so the text runs left to right, and dashed, not yet a message. */
.bq-demo-msg--draft {
    align-self: flex-start;
    max-width: 85%;

    .bq-demo-bubble {
        border-style: dashed;
        background: @pink-bg;
        box-shadow: none;
    }
}

.bq-demo-bubble {
    min-width: 0;
    border: 1px solid @line;
    background: @paper;
    padding: 0.65rem 0.9rem;
    line-height: 1.55;
    overflow-x: auto;
    box-shadow: 2px 2px var(--bq-shadow);

    /* same bunny-mark treatment as the real reply bubbles (chat.css) */
    .bq-demo-bunny {
        display: block;
        height: 2.6em;
    }

    /* The bunny, with the waiting dots beside it while it thinks. The row is
       there from the first frame to the last, so the bunny never moves. */
    .bq-demo-bubble-head {
        display: flex;
        align-items: center;
        gap: 0.4rem;
    }

    p {
        margin: 0 0 0.6rem;
    }

    p:last-child {
        margin: 0;
    }
}

.bq-demo-table {
    border-collapse: collapse;
    margin: 0 0 0.6rem;
    font-size: 0.8rem;

    th,
    td {
        border: 1px solid @line;
        padding: 0.3rem 0.6rem;
        text-align: left;
    }

    th {
        background: var(--bq-alt-bg);
        font-weight: 600;
    }
}

.bq-demo-file {
    display: inline-block;
    padding: 0.3rem 0.7rem;
    border: 1px solid @line;
    color: @pink;
    font-size: 0.8rem;
}

/* The per-file citation line under the table. */
.bq-demo-sources {
    font-size: 0.72rem;
    color: @muted;
}

/* Filled folder glyph, like the real attachment chip's folder (is-filled). */
.bq-demo-folder-icon {
    font-size: 1.15em;
    font-variation-settings: "FILL" 1;
    margin-right: 0.25rem;
}

/* The typing caret, at the end of the question as it is written. */
.bq-demo-caret {
    display: inline-block;
    width: 1px;
    height: 1.1em;
    margin-left: 2px;
    vertical-align: -0.18em;
    background: @ink;
    animation: bqd-blink 1s steps(2) infinite;
}

/* The real widget's dot-trail: solid ".", "..", "..." stepped, no fading. The
   box is always three dots wide, so whatever holds it (the bunny's bubble,
   the folder's row) keeps one width while the trail runs. */
.bq-demo-dots {
    display: inline-block;
    min-width: calc(3ch + 0.6em);
    color: @muted;
    letter-spacing: 0.2em;
    text-align: left;

    &::after {
        content: ".";
        animation: bqd-dots 1.2s steps(1) infinite;
    }
}

.bq-demo-note .bq-demo-dots {
    color: @warning;
}

@keyframes bqd-blink {
    50% {
        opacity: 0;
    }
}

@keyframes bqd-dots {
    0% {
        content: ".";
    }

    33% {
        content: "..";
    }

    66% {
        content: "...";
    }
}

@media (prefers-reduced-motion: no-preference) {
    /* Each piece slides in as the script inserts it. The bunny's bubble is
       inserted once, when it starts thinking, and then only grows. */
    .bq-demo-note,
    .bq-demo-msg {
        animation: bqd-in 0.28s ease;
    }

    /* A line of the reply fades in where it lands. No movement, and nothing
       on the table's rows: a row that fades shows its borders before its
       text. */
    .bq-demo-reply-p,
    .bq-demo-sources,
    .bq-demo-file {
        animation: bqd-fade 0.2s ease both;
    }
}

@keyframes bqd-fade {
    from {
        opacity: 0;
    }
}

@keyframes bqd-in {
    from {
        opacity: 0;
        transform: translateY(6px);
    }

    to {
        opacity: 1;
        transform: none;
    }
}

/* under the window and its plate */
.bq-demo-caption {
    margin: 1.9rem 0 0;
    color: @muted;
    font-size: 0.88rem;
    text-align: center;
}

/* ─── sections ─── */
.bq-section {
    padding: 5.5rem 0;
}

/* Every other section is a band one step off the paper. */
.bq-section--alt {
    background: var(--bq-alt-bg);
    border-top: 1px solid @line;
    border-bottom: 1px solid @line;
}

/* Title on the left, the supporting line on the right, level with the
   title's last line. */
.bq-section-header {
    display: grid;
    grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
    gap: 1rem 3.5rem;
    align-items: end;
    margin-bottom: 3rem;

    h2 {
        margin: 0;
        font-size: clamp(1.7rem, 3.4vw, 2.6rem);
        line-height: 1.14;
        font-weight: 700;
        letter-spacing: -0.01em;
        text-shadow: var(--bq-text-shadow);
    }
}

.bq-section-sub {
    margin: 0 0 0.3rem;
    color: @muted;
}

// Layout only. The "All articles →" link inside it is a plain text link and
// follows the site rule through .bq-cta-link: ink at rest, underlined, pink on
// hover, arrow undecorated.
.bq-section-link {
    margin: 1.75rem 0 0;
    font-size: 0.9rem;
}

/* ─── glyphs ─── */
/* Bare decorative marks. No box, border, or shadow: a framed tile read as a
   button, and these are not clickable. */
.bq-icon {
    display: inline-flex;
    flex-shrink: 0;
    color: @ink;

    :deep(svg) {
        display: block;
    }
}

/* ─── the drawings' vocabulary ─── */
.bq-landing svg {
    stroke-linecap: round;
    stroke-linejoin: round;
}

.ill-shadow {
    fill: var(--bq-shadow);
}

.ill-surface {
    fill: @paper;
    stroke: @ink;
    stroke-width: 1.5;
}

.ill-field {
    fill: @paper;
    stroke: @line;
    stroke-width: 1;
}

.ill-ink {
    stroke: @ink;
    stroke-width: 1.5;
}

.ill-ink--thick {
    stroke-width: 2.5;
}

.ill-line {
    stroke: @muted;
    stroke-width: 1;
    opacity: 0.6;
}

.ill-accent {
    stroke: @pink;
    stroke-width: 2;
}

/* short dashes running along a line, in the direction it was drawn */
.ill-flow {
    stroke-dasharray: 3 9;
    animation: bq-ill-flow 1.2s linear infinite;
}

@keyframes bq-ill-flow {
    to {
        stroke-dashoffset: -12;
    }
}

.ill-caret {
    animation: bqd-blink 1s steps(2) infinite;
}

.ill-spark {
    fill: @pink;
}

.ill-bubble {
    fill: @paper;
    stroke: @line;
    stroke-width: 1;
}

.ill-bubble--user {
    fill: @pink-bg;
    stroke: @pink;
}

.ill-send {
    fill: @ink;
}

/* ─── steps ─── */
.bq-step-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1.75rem;
}

.bq-step {
    display: flex;
    flex-direction: column;
    min-width: 0;
    border: 1px solid @line;
    background: @paper;
    box-shadow: 2px 2px var(--bq-shadow);
}

/* The drawing's own panel: the brand tint under a dot grid. */
.bq-step-art {
    position: relative;
    padding: 1.25rem 1rem;
    border-bottom: 1px solid @line;
    background-color: @pink-bg;
    .dot-grid();

    svg {
        display: block;
        width: 100%;
        max-width: 300px;
        height: auto;
        margin: 0 auto;
    }
}

/* A pink chevron in the gutter between one step and the next. */
.bq-step:not(:last-child) .bq-step-art::after {
    content: "";
    position: absolute;
    top: 50%;
    right: calc(-0.875rem - 0.3rem - 1px);
    width: 0.6rem;
    height: 0.6rem;
    margin-top: -0.3rem;
    border-top: 2px solid @pink;
    border-right: 2px solid @pink;
    transform: rotate(45deg);
}

.bq-step-body {
    padding: 1.5rem 1.5rem 1.65rem;

    h3 {
        margin: 0 0 0.5rem;
        font-size: 1.08rem;
        line-height: 1.35;
    }

    p {
        margin: 0;
        color: @muted;
        font-size: 0.92rem;
        line-height: 1.6;
    }
}

/* ─── why ─── */
.bq-why-grid {
    display: grid;
    grid-template-columns: minmax(0, 0.82fr) minmax(0, 1.18fr);
    gap: 4rem;
    align-items: start;
}

/* The bunny's panel. */
.bq-why-scene {
    min-width: 0;
    border: 1px solid @line;
    background-color: @pink-bg;
    .dot-grid(18px);
    box-shadow: 4px 4px var(--bq-shadow);

    svg {
        display: block;
        width: 100%;
        height: auto;
    }
}

.ill-orbit {
    stroke: @muted;
    stroke-width: 1;
    stroke-dasharray: 2 8;
    opacity: 0.7;
    animation: bq-ill-orbit 30s linear infinite;
}

.ill-orbit--inner {
    stroke: @pink;
    stroke-dasharray: 5 7;
    opacity: 0.55;
    animation-direction: reverse;
}

/* the dashes circle the bunny: one full dash period per second and a half */
@keyframes bq-ill-orbit {
    to {
        stroke-dashoffset: -200;
    }
}

.ill-tile {
    fill: @paper;
    stroke: @line;
    stroke-width: 1;
}

.ill-glyph {
    stroke: @ink;
    stroke-width: 2;
}

/* the bunny breathes */
.ill-bunny {
    animation: bq-ill-bob 4.5s ease-in-out infinite;
}

@keyframes bq-ill-bob {
    0%,
    100% {
        transform: translateY(0);
    }

    50% {
        transform: translateY(-6px);
    }
}

.ill-twinkle {
    animation: bq-ill-twinkle 2.6s ease-in-out infinite;
}

.ill-twinkle--late {
    animation-delay: 0.9s;
}

.ill-twinkle--later {
    animation-delay: 1.7s;
}

@keyframes bq-ill-twinkle {
    0%,
    100% {
        opacity: 1;
    }

    50% {
        opacity: 0.2;
    }
}

.bq-reasons {
    display: grid;
    /* the track can shrink below its content's natural width */
    grid-template-columns: minmax(0, 1fr);
    gap: 0;
}

.bq-reason {
    display: flex;
    gap: 1.25rem;
    /* flex-start, not baseline: the icon has no useful text baseline. The
       icon instead carries a tiny offset that centers it on the title's
       FIRST LINE box (1.05rem font at the page's 1.7 line height = 1.785rem
       tall). */
    align-items: flex-start;
    min-width: 0;
    border-bottom: 1px solid @line;
    padding: 1.5rem 0;

    &:first-child {
        padding-top: 0;
    }

    &:last-child {
        border-bottom: none;
        padding-bottom: 0;
    }

    p {
        margin: 0.35rem 0 0;
        color: @muted;
        font-size: 0.93rem;
        line-height: 1.65;
    }

    /* (1.785rem line - 24px glyph) / 2 */
    .bq-icon {
        margin-top: 0.14rem;
    }
}

.bq-reason-body {
    flex: 1;
    min-width: 0;
}

.bq-reason-title {
    display: block;
    font-weight: 600;
    font-size: 1.05rem;
}

/* ─── for developers ─── */
/* The lockup is an inline-flex box; on a line of its own between the pill and
   the heading. */
.bq-dev-lockup {
    display: flex;
    margin: 0.25rem 0 1rem;
}

/* ─── cards (developers, news) ─── */
.bq-card-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1.5rem;
}

.bq-card {
    /* The card itself is the anchor; a column so the link line can be pinned
       to the bottom edge whatever the length of the copy above it. */
    display: flex;
    flex-direction: column;
    min-width: 0;
    padding: 1.9rem 1.6rem 1.6rem;
    border: 1px solid @line;
    border-top: 2px solid @pink;
    background: @paper;
    box-shadow: 2px 2px var(--bq-shadow);
    color: @ink;
    text-decoration: none;
    transition: transform 0.12s ease, box-shadow 0.12s ease;

    &:hover {
        text-decoration: none;
    }

    /* the lift only where hover is a real pointer state: a tap would leave a
       touch screen's card stuck raised */
    @media (hover: hover) and (pointer: fine) {
        &:hover {
            transform: translate(-1px, -1px);
            box-shadow: 4px 4px var(--bq-shadow);
        }
    }

    .bq-icon {
        /* as a flex item it would otherwise stretch to the card's width */
        align-self: flex-start;
        margin-bottom: 1rem;

        :deep(svg) {
            width: 28px;
            height: 28px;
        }
    }

    h3 {
        margin: 0 0 0.55rem;
        font-size: 1.08rem;
        line-height: 1.3;
    }

    p {
        margin: 0;
        color: @muted;
        font-size: 0.94rem;
        line-height: 1.6;
    }
}

/* A span, since the card is already the anchor (anchors can't nest): plain
   coloured text, no hover underline. margin-top:auto parks it on the card's
   bottom edge so the three link lines sit level; the gap above it is padding,
   because an auto margin IS the free space it absorbs and the tallest card
   has none left. */
.bq-card-more {
    margin-top: auto;
    padding-top: 1.25rem;
    color: @pink;
    font-size: 0.9rem;
}

.bq-news-date {
    margin-bottom: 0.6rem;
    color: @muted;
    font-size: 0.78rem;
}

.bq-news-excerpt {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

/* ─── pricing ─── */
/* The plan cards are PlanCards' own markup. The landing restyles them into its
   card language (paper, hairline, 2px offset shadow, the same lift on hover as
   the developer and news cards) and makes each one a single link. */
.bq-plans.bq-pricing-cards {
    max-width: none;
    /* the component clips its overflow, a carousel leftover, which would cut
       off the hover shadow of the last card in a row */
    overflow: visible;
    /* room for the POPULAR badge that sits on the Premium card's top edge */
    padding-top: 0.75rem;

    :deep(.bq-plans-grid) {
        gap: 1.5rem;
    }

    :deep(.bq-plan-card) {
        /* the anchor for the stretched link below */
        position: relative;
        background: @paper;
        box-shadow: 2px 2px var(--bq-shadow);
        transition: transform 0.12s ease, box-shadow 0.12s ease, border-color 0.15s;
    }

    /* the lift only where hover is a real pointer state, as on the buttons:
       a tap would leave a touch screen's card stuck raised */
    @media (hover: hover) and (pointer: fine) {
        :deep(.bq-plan-card:hover) {
            transform: translate(-1px, -1px);
            box-shadow: 4px 4px var(--bq-shadow);
        }
    }

    :deep(.bq-plan-card:active) {
        transform: translate(1px, 1px);
        box-shadow: 1px 1px var(--bq-shadow);
        transition-duration: 0.04s;
    }
}

/* The card's one link. Unstyled itself: its ::after covers the whole card, so
   a click anywhere on the card follows it. The visible button is a span inside
   it, not the anchor, because the global a.bq-btn hover transforms the anchor,
   and a transformed anchor would shrink its ::after to the button's box. */
.bq-plan-link {
    display: block;
    color: inherit;
    text-decoration: none;

    &:hover {
        text-decoration: none;
    }

    &::after {
        content: "";
        position: absolute;
        inset: 0;
        z-index: 1;
    }

    /* the focus ring goes round the card, which is what the link stands for */
    &:focus-visible {
        outline: none;
    }

    &:focus-visible::after {
        outline: 2px solid @pink;
        outline-offset: 3px;
    }
}

.bq-plan-cta {
    display: block;
    width: 100%;
    box-sizing: border-box;
    padding: 0.8rem 1rem;
    text-align: center;
    white-space: nowrap;
}

/* Free and Standard: outlined, so Premium's filled button is the one that
   stands out, like its accent border. */
.bq-plan-cta--ghost {
    background: @paper;
    border-color: @ink;
    color: @ink;
}

/* ─── closing panel ─── */
.bq-close-panel {
    position: relative;
    isolation: isolate;
    padding: 4rem 1.5rem 4.75rem;
    border: 1px solid @line;
    background: @pink-bg;
    box-shadow: 4px 4px var(--bq-shadow);
    text-align: center;
    overflow: hidden;

    /* the glyph field, clear in the middle where the words are */
    &::before {
        content: "";
        position: absolute;
        inset: 0;
        z-index: -1;
        pointer-events: none;
        background-color: @line;
        -webkit-mask-image: radial-gradient(55% 75% at 50% 50%, transparent 55%, #000), @glyphs;
        mask-image: radial-gradient(55% 75% at 50% 50%, transparent 55%, #000), @glyphs;
        -webkit-mask-size: 100% 100%, 180px 180px;
        mask-size: 100% 100%, 180px 180px;
        -webkit-mask-repeat: no-repeat, repeat;
        mask-repeat: no-repeat, repeat;
        -webkit-mask-composite: source-in;
        mask-composite: intersect;
    }

    h2 {
        margin: 0 0 0.75rem;
        font-size: clamp(1.9rem, 4vw, 2.9rem);
        line-height: 1.15;
        font-weight: 700;
        text-shadow: var(--bq-text-shadow);
    }

    p {
        margin: 0 0 2.25rem;
        color: @muted;
    }
}

/* the hopping bunny above the closing line */
.bq-landing-bunny {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 1.25rem;
}

/* ─── motion ─── */
@media (prefers-reduced-motion: reduce) {
    .ill-flow,
    .ill-caret,
    .ill-orbit,
    .ill-bunny,
    .ill-twinkle {
        animation: none;
    }
}

/* ─── responsive ─── */
@media (max-width: 1000px) {
    /* The bunny's track already puts a jump's worth of air above the chat
       window, so the gap between the copy and the stage can be small. */
    .bq-hero-grid {
        grid-template-columns: minmax(0, 1fr);
        gap: 1rem;
    }

    /* One column: the copy now runs the full width, so there is no clear
       corner left for the glyph field to sit in. */
    .bq-hero::before {
        display: none;
    }

    /* stacked, the chat window is as wide as the old centred one */
    .bq-hero-stage {
        width: 100%;
        max-width: 780px;
        margin: 0 auto;
    }

    .bq-section-header {
        grid-template-columns: minmax(0, 1fr);
        align-items: start;
    }

    .bq-why-grid {
        grid-template-columns: minmax(0, 1fr);
        gap: 2.5rem;
    }

    .bq-why-scene {
        width: 100%;
        max-width: 380px;
        margin: 0 auto;
    }
}

@media (max-width: 900px) {
    .bq-wrap {
        padding: 0 1.5rem;
    }

    .bq-hero {
        padding: 2.25rem 0 3.25rem;
    }

    .bq-section {
        padding: 3.5rem 0;
    }

    .bq-section-header {
        margin-bottom: 2.25rem;
    }

    .bq-step-grid,
    .bq-card-grid {
        grid-template-columns: minmax(0, 1fr);
    }

    /* stacked, there is no gutter for the chevron to sit in */
    .bq-step:not(:last-child) .bq-step-art::after {
        display: none;
    }

    .bq-demo-msg {
        max-width: 100%;
    }
}

@media (max-width: 640px) {
    /* the plate would push past the gutter */
    .bq-demo-frame::before {
        inset: 8px -8px -8px 8px;
    }

    .bq-hero-actions .bq-btn,
    .bq-close-panel .bq-btn {
        display: block;
        width: 100%;
        box-sizing: border-box;
        text-align: center;
    }

    .bq-close-panel {
        padding: 3rem 1.25rem 3.25rem;
    }
}

@media (max-width: 500px) {
    .bq-reason {
        flex-direction: column;
        gap: 0.5rem;
    }

    /* Stacked, there is no first line to sit level with. */
    .bq-reason .bq-icon {
        margin-top: 0;
    }
}
</style>
